'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ConceptionCalculator() {
  const t = useTranslations("tools.conceptionCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    lastPeriod: '',
    cycleLength: 28
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setInputs(prev => ({
      ...prev,
      lastPeriod: thirtyDaysAgo.toISOString().split('T')[0]
    }));
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatDateShort = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputs.lastPeriod) return;

    const lmp = new Date(inputs.lastPeriod);
    const cycle = parseInt(inputs.cycleLength.toString());

    const ovulationDay = cycle - 14;
    const ovulationDate = addDays(lmp, ovulationDay);

    const fertileStart = addDays(ovulationDate, -5);
    const fertileEnd = ovulationDate;

    const mostFertileStart = addDays(ovulationDate, -2);
    const mostFertileEnd = ovulationDate;

    const dueDate = addDays(lmp, 280);

    const cycles = [];
    for (let i = 0; i < 6; i++) {
      const periodStart = addDays(lmp, cycle * i);
      const ovulation = addDays(periodStart, cycle - 14);
      const due = addDays(periodStart, 280);

      cycles.push({
        number: i + 1,
        periodStart,
        ovulation,
        dueDate: due
      });
    }

    setResults({
      ovulationDate,
      fertileStart,
      fertileEnd,
      mostFertileStart,
      mostFertileEnd,
      dueDate,
      cycles
    });
  };

  const colors = {
    primary: '#ec4899',
    primaryHover: '#db2777',
    secondary: '#fce7f3',
    accent: '#f9a8d4',
    textPrimary: '#1f2937',
    textSecondary: '#6b7280',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(236, 72, 153, 0.2)',
    success: '#10b981',
    successBg: '#f0fdf4',
    info: '#3b82f6',
    infoBg: '#eff6ff',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3f2 0%, #fce7f3 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '1.75rem' },
    {}
  );

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.875rem',
    border: '2px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    background: '#f9fafb',
    boxSizing: 'border-box'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem', borderRadius: '0.75rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3f2 0%, #fce7f3 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: `4px solid ${colors.primary}`
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const fertileWindowCardStyle = {
    ...resultCardStyle,
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderLeft: `4px solid ${colors.success}`
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f3f4f6'
  };

  const resultLabelStyle: React.CSSProperties = {
    color: colors.textSecondary,
    fontSize: '0.95rem'
  };

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    {}
  );

  const resultValueHighlightStyle = {
    ...resultValueStyle,
    color: colors.primary,
    fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem'
  };

  const cycleTableStyle: React.CSSProperties = {
    width: '100%',
    marginTop: '1rem',
    borderCollapse: 'collapse'
  };

  const tableHeaderStyle: React.CSSProperties = {
    background: '#f9fafb',
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: screenSize === 'mobile' ? '0.8rem' : '0.875rem',
    color: colors.textSecondary,
    fontWeight: 600,
    borderBottom: '2px solid #e5e7eb'
  };

  const tableCellStyle: React.CSSProperties = {
    padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: screenSize === 'mobile' ? '0.8rem' : '0.9rem',
    color: '#374151'
  };

  const infoBoxStyle = getResponsiveStyle(
    {
      background: colors.infoBg,
      borderLeft: `4px solid ${colors.info}`,
      borderRadius: '0.5rem',
      marginTop: '1.5rem'
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    {}
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(236, 72, 153, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const listStyle: React.CSSProperties = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const refListStyle: React.CSSProperties = {
    listStyle: 'decimal',
    marginLeft: '1.5rem'
  };

  const refItemStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0.75rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="last-period" style={labelStyle}>
                {t("lastPeriodLabel")}
              </label>
              <input
                type="date"
                id="last-period"
                value={inputs.lastPeriod}
                onChange={(e) => setInputs({...inputs, lastPeriod: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
                required
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="cycle-length" style={labelStyle}>
                {t("cycleLengthLabel")}
              </label>
              <select
                id="cycle-length"
                value={inputs.cycleLength}
                onChange={(e) => setInputs({...inputs, cycleLength: parseInt(e.target.value)})}
                required
                style={inputStyle}
              >
                {[...Array(19)].map((_, i) => {
                  const days = 22 + i;
                  return (
                    <option key={days} value={days}>
                      {days} {t("days")}
                    </option>
                  );
                })}
              </select>
            </div>

            <button type="submit" style={calculateButtonStyle}>
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{...h2Style, textAlign: 'center' as const, marginBottom: '1.5rem'}}>
                {t("resultsTitle")}
              </h3>

              <div style={fertileWindowCardStyle}>
                <h4 style={{...h3Style, color: colors.success, marginTop: 0}}>
                  {t("fertileWindowTitle")}
                </h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("ovulationDate")}</span>
                  <span style={resultValueHighlightStyle}>{formatDate(results.ovulationDate)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("fertileWindow")}</span>
                  <span style={resultValueStyle}>
                    {formatDateShort(results.fertileStart)} - {formatDateShort(results.fertileEnd)}
                  </span>
                </div>
                <div style={{...resultItemStyle, borderBottom: 'none'}}>
                  <span style={resultLabelStyle}>{t("mostFertileDays")}</span>
                  <span style={resultValueStyle}>
                    {formatDateShort(results.mostFertileStart)} - {formatDateShort(results.mostFertileEnd)}
                  </span>
                </div>
              </div>

              <div style={resultCardStyle}>
                <h4 style={{...h3Style, color: colors.primary, marginTop: 0}}>
                  {t("dueDateTitle")}
                </h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("estimatedDueDate")}</span>
                  <span style={resultValueHighlightStyle}>{formatDate(results.dueDate)}</span>
                </div>
                <div style={{...resultItemStyle, borderBottom: 'none'}}>
                  <span style={resultLabelStyle}>{t("conceptionToDueDate")}</span>
                  <span style={resultValueStyle}>{t("daysWeeks")}</span>
                </div>
              </div>

              <div style={resultCardStyle}>
                <h4 style={{...h3Style, color: colors.primary, marginTop: 0}}>
                  {t("nextCyclesTitle")}
                </h4>
                <div style={{overflowX: 'auto'}}>
                  <table style={cycleTableStyle}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>{t("cycle")}</th>
                        <th style={tableHeaderStyle}>{t("periodStart")}</th>
                        <th style={tableHeaderStyle}>{t("ovulation")}</th>
                        <th style={tableHeaderStyle}>{t("dueDate")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.cycles.map((cycle: any) => (
                        <tr key={cycle.number} style={{cursor: 'default'}}>
                          <td style={tableCellStyle}>{cycle.number}</td>
                          <td style={tableCellStyle}>{formatDateShort(cycle.periodStart)}</td>
                          <td style={tableCellStyle}>{formatDateShort(cycle.ovulation)}</td>
                          <td style={tableCellStyle}>{formatDateShort(cycle.dueDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={infoBoxStyle}>
                <p style={{color: '#1e40af', fontSize: '0.9rem', margin: 0, lineHeight: 1.6}}>
                  <strong>💡 {t("noteTitle")}</strong> {t("noteText")}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>

        <h3 style={h3Style}>{t("insightAccuracyTitle")}</h3>
        <p style={paragraphStyle}>{t("insightAccuracyText")}</p>

        <h3 style={h3Style}>{t("insightUltrasoundTitle")}</h3>
        <p style={paragraphStyle}>{t("insightUltrasoundText")}</p>

        <h3 style={h3Style}>{t("insightFertilityTitle")}</h3>
        <p style={paragraphStyle}>{t("insightFertilityText")}</p>

        <h3 style={h3Style}>{t("insightIrregularTitle")}</h3>
        <p style={paragraphStyle}>{t("insightIrregularText")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailHowItWorksTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailHowItWorks1")}</li>
          <li style={listItemStyle}>{t("detailHowItWorks2")}</li>
          <li style={listItemStyle}>{t("detailHowItWorks3")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailBestPracticesTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailBestPractice1")}</li>
          <li style={listItemStyle}>{t("detailBestPractice2")}</li>
          <li style={listItemStyle}>{t("detailBestPractice3")}</li>
          <li style={listItemStyle}>{t("detailBestPractice4")}</li>
          <li style={listItemStyle}>{t("detailBestPractice5")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailLimitationsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailLimitation1")}</li>
          <li style={listItemStyle}>{t("detailLimitation2")}</li>
          <li style={listItemStyle}>{t("detailLimitation3")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={paragraphStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={refListStyle}>
          <li style={refItemStyle}>
            <a href="https://www.indiraivf.com/conception-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref1")}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.essentiahealth.org/about/essentia-health-blog/why-you-might-want-to-use-a-pregnancy-due-date-calculator-and-how-to-use-it/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref2")}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.acog.org" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref3")}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.mayoclinic.org/healthy-lifestyle/getting-pregnant/in-depth/fertility/art-20047584" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("ref4")}
            </a>
          </li>
        </ol>

        <div style={infoBoxStyle}>
          <p style={{color: '#1e40af', fontSize: '0.9rem', margin: 0, lineHeight: 1.6}}>
            <strong>⚠️ {t("disclaimerTitle")}</strong> {t("disclaimerText")}
          </p>
        </div>
      </section>
    </div>
  );
}